import { useEffect, useState } from "react";
import { FileSignature, Download, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Rappel du tableau de bord : affiché tant que l'utilisateur n'a pas de convention signée. */
export function ConventionReminder() {
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    authFetch(`${API_BASE}/conventions/me`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMissing(Array.isArray(d) && d.length === 0))
      .catch(() => {});
  }, []);
  if (!missing) return null;
  return (
    <div className="rounded-2xl p-4 flex items-center gap-3 flex-wrap" style={{ background: "#FCF1ED", border: "1px solid #F3D3C6" }} data-testid="convention-reminder">
      <FileSignature className="w-6 h-6 shrink-0" style={{ color: "#B4532F" }} />
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-bold" style={{ color: "#5B2A1C" }}>Finalisez votre inscription</p>
        <p className="text-xs" style={{ color: "#8A5A4A" }}>
          Signez votre convention en ligne, en quelques minutes, pour utiliser Véyèr CIEL gratuitement jusqu'au 29 janvier 2027.
        </p>
      </div>
      <Button className="rounded-xl gap-2 text-white shrink-0" style={{ background: "#2D6A4F" }}
        onClick={() => { window.location.href = "/convention"; }}>
        <FileSignature className="w-4 h-4" /> Signer ma convention
      </Button>
    </div>
  );
}

/**
 * « Ma convention » (Mon compte) : l'utilisateur signe sa convention en autonomie sur la
 * page /convention, puis retrouve ici la convention signée et son PDF.
 */
export default function MyConventionSection() {
  const [convs, setConvs] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    authFetch(`${API_BASE}/conventions/me`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setConvs)
      .catch(() => setConvs([]));
  }, []);

  // Lien direct « /account#convention » : amène la section à l'écran une fois chargée.
  useEffect(() => {
    if (convs !== null && window.location.hash === "#convention") {
      document.getElementById("convention")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [convs]);

  const download = async (ref) => {
    setDownloading(ref);
    try {
      const res = await authFetch(`${API_BASE}/conventions/me/${encodeURIComponent(ref)}/pdf`);
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `Convention-Veyer-CIEL_${ref}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Téléchargement impossible");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div id="convention" className="rounded-2xl p-6 mb-4" style={{ background: "white", border: "1px solid #E5E5E5" }} data-testid="my-convention">
      <h2 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: "#1A1A1A" }}>
        <FileSignature className="w-5 h-5" style={{ color: "#2D6A4F" }} /> Ma convention
      </h2>

      {convs === null && <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#2D6A4F" }} />}

      {convs && convs.length === 0 && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: "#4A4A4A" }}>
            Signez votre convention en ligne pour finaliser votre inscription et utiliser Véyèr CIEL
            gratuitement jusqu'au <strong>29 janvier 2027</strong>. Cela prend quelques minutes ; vous
            recevrez votre exemplaire signé en PDF par e-mail.
          </p>
          <Button className="rounded-xl gap-2 text-white" style={{ background: "#2D6A4F" }}
            onClick={() => { window.location.href = "/convention"; }} data-testid="sign-convention-btn">
            <FileSignature className="w-4 h-4" /> Signer ma convention
          </Button>
        </div>
      )}

      {convs && convs.length > 0 && (
        <div className="space-y-2">
          {convs.map((c) => (
            <div key={c.reference} className="flex items-center gap-3 rounded-xl p-3" style={{ background: "#F3F7F2" }}>
              <CheckCircle className="w-5 h-5 shrink-0" style={{ color: "#2D6A4F" }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: "#1A1A1A" }}>
                  Convention signée{c.exploitation ? ` · ${c.exploitation}` : ""}
                </p>
                <p className="text-xs" style={{ color: "#7A7A7A" }}>
                  {c.ligne?.Date ? `Le ${c.ligne.Date}` : ""}{c.signataire ? ` par ${c.signataire}` : ""} · Réf. {c.reference}
                </p>
              </div>
              <Button variant="outline" size="sm" className="rounded-lg gap-1 text-xs shrink-0"
                onClick={() => download(c.reference)} disabled={downloading === c.reference}>
                {downloading === c.reference ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
